// `ConvFilters` — Wave 5c.1.
//
// Account filter (US-063) + AI-mode tab filter (US-064).
//
// The DS-D `ConvList` composite already renders the account filter pill row
// AND the mode-tab strip — those are part of its surface. This component is
// kept around for two reasons:
//
//   1. Unit-testability — a thin React surface around the filter state means
//      we can render the filter row in isolation in Storybook / RTL without
//      mounting the entire `ConvList`.
//   2. Coordination contract — 5c.2 / 5c.3 / 5d.x can `useConvFilters()` to
//      read the same filter shape (driven from URL search params) without
//      duplicating the parsing.
//
// Today the InboxPage threads the filter state directly into `ConvList`, so
// this module just re-exports the type definitions and a tiny URL-search hook.

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

export type ConvModeTab = 'all' | 'action' | 'auto' | 'review' | 'offtarget';

export interface ConvFiltersState {
  /** 'all' OR a LinkedIn-account id. */
  accountId: string;
  /** Mode tab — defaults to 'all'. */
  mode: ConvModeTab;
}

export const DEFAULT_FILTERS: ConvFiltersState = {
  accountId: 'all',
  mode: 'all',
};

const MODE_VALUES: readonly ConvModeTab[] = ['all', 'action', 'auto', 'review', 'offtarget'];

function parseMode(raw: unknown): ConvModeTab {
  if (typeof raw !== 'string') return 'all';
  return (MODE_VALUES as readonly string[]).includes(raw) ? (raw as ConvModeTab) : 'all';
}

function parseAccountId(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length === 0) return 'all';
  return raw;
}

export interface InboxSearchSchema {
  accountId?: string;
  mode?: string;
  selectedId?: string;
}

/**
 * `useInboxFilters()` — read+write the filter state from the route search
 * params (`/inbox?accountId=…&mode=…&selectedId=…`). Tests can pass an
 * explicit `initial` to bootstrap the state without a router fixture.
 */
export function useInboxFilters() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as InboxSearchSchema;
  const accountId = parseAccountId(search.accountId);
  const mode = parseMode(search.mode);
  const selectedId = typeof search.selectedId === 'string' ? search.selectedId : null;

  const setAccountId = useCallback(
    (next: string) => {
      void navigate({
        to: '/inbox',
        search: (prev) => {
          const merged = { ...(prev as Record<string, unknown>) } as InboxSearchSchema;
          merged.accountId = next === 'all' ? undefined : next;
          return merged as never;
        },
        replace: true,
      });
    },
    [navigate],
  );
  const setMode = useCallback(
    (next: ConvModeTab) => {
      void navigate({
        to: '/inbox',
        search: (prev) => {
          const merged = { ...(prev as Record<string, unknown>) } as InboxSearchSchema;
          merged.mode = next === 'all' ? undefined : next;
          return merged as never;
        },
        replace: true,
      });
    },
    [navigate],
  );
  const setSelectedId = useCallback(
    (next: string | null) => {
      void navigate({
        to: '/inbox',
        search: (prev) => {
          const merged = { ...(prev as Record<string, unknown>) } as InboxSearchSchema;
          merged.selectedId = next ?? undefined;
          return merged as never;
        },
        replace: true,
      });
    },
    [navigate],
  );

  return {
    accountId,
    mode,
    selectedId,
    setAccountId,
    setMode,
    setSelectedId,
  };
}
